import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import styled from 'styled-components/macro';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import DataTable, { createTheme } from 'react-data-table-component';
import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { GET_CLINIC_FINANCIAL_REPORT_SETTINGS } from 'shared/queries';
import format from 'date-fns/format';
import {
  PimsWritebackStatus,
  ReportFilterTab,
  StripePayoutSelectionFragment,
  SortOrder,
  PaymentReportFinancialTransactionFragment,
  useGetDisbursementsQuery,
  useGetFinancialTransactionsQuery,
  GetFinancialTransactionsQuery,
  useGetDisputesQuery,
  StripeDisputeSelectionFragment,
} from 'shared/types/graphql';
import { useReactToPrint } from 'react-to-print';
import useClinicUser from 'shared/hooks/useClinicUser';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { UPDATE_CLINIC_FINANCIAL_REPORT_SETTINGS } from 'shared/mutations';
import cloneDeep from 'lodash-es/cloneDeep';
import RefundModal from 'shared/components/RefundModal';
import { useSidePanelSearch } from 'shared/components/SidePanel/components/SearchPanel/state/providers/SearchPanelProvider';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import usePersistedState from 'shared/hooks/usePersistedState';
import DevModeDisplayJSON from 'pages/Settings/components/DevModeDisplayJSON';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { IColumnSelection, PaymentReportQueriesVariables } from './types';
import { getWritebackStatus } from './utils';
import { PaymentReportColumnNames, PaymentReportColumnSelectors } from './enums';
import WritebackAlerts from './components/WritebackAlerts';
import usePaymentWritebackConfig from 'shared/hooks/usePaymentWritebackConfig';
import { CareMembershipEvents } from 'shared/providers/TrackingProvider/EventNames';
import TerminalFilterMenu from './components/TerminalFilterMenu';
import EODBanner from './components/EODBanner';
import {
  Box,
  CModal,
  CModalBody,
  CModalHeader,
  Flex,
  Grid,
  GridItem,
  ModalContent,
  ModalOverlay,
  useBreakpoint,
  useDisclosure,
} from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { ModalCloseButton } from '@televet/kibble-ui/build/components/Modal/components/ModalCloseButton';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Checkbox } from '@televet/kibble-ui/build/components/Checkbox';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { initialTransactionsColumnsSelection, PaymentReportStripePaymentIntent } from './records/transactions.records';
import { initialDisbursementsColumnsSelection } from './records/disbursements.records';
import { buildDisbursementsData } from './utils/buildDisbursementsData';
import { buildTransactionsData } from './utils/buildTransactionsData';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { FilterTab } from './components/FilterTab';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { TotalsTable } from './components/TotalsTable';
import { replaceParam, RouteDefinitions } from 'routes';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { openChannelOverlay } from 'pages/Conversations/state/conversationsSlice';
import { useAppDispatch } from 'state/hooks';
import useDebounce from 'shared/hooks/useDebounce';
import sortBy from 'lodash-es/sortBy';
import uniqBy from 'lodash-es/uniqBy';
import ColumnFilterMenu from './components/ColumnFilterMenu';
import { useTracking } from 'shared/providers/TrackingProvider';
import { buildDisputesData } from './utils/buildDisputesData';
import { initialDisputesColumnsSelection } from './records/disputes.records';
import { useDisputeNotification } from 'shared/hooks/useDisputeNotification';
import { DisputeDrawer } from './components/DisputeDrawer';
import { ConnectComponentsProvider } from '@stripe/react-connect-js';
import { useStripeAccountSession } from 'shared/hooks/useStripeAccountSession';
import { ReportFilters } from './components/ReportFilters';
import { ExportMenu } from './components/ExportMenu';
import { useLocation } from 'react-router-dom';
import { TimePicker } from '@televet/kibble-ui/build/components/TimePicker';
import { Popover } from '@televet/kibble-ui/build/components/Popover';

const DEFAULT_TAKE = 10;

const tabs = {
  disputes: ReportFilterTab.Disputes,
  disbursements: ReportFilterTab.Disbursements,
  complete: ReportFilterTab.Complete,
  all: ReportFilterTab.All,
  incomplete: ReportFilterTab.Incomplete,
};

const PaymentReport = (): JSX.Element => {
  // Hooks
  const { currentClinic, currentClinicId, hasAdminRoleAtCurrentClinic, clinicUser } = useClinicUser();
  const toast = useToast();
  const dispatch = useAppDispatch();
  const { isClientIdSupported, primaryIntegrationName, isInvoiceDiscoverySupported } = useIntegrationsProvider();
  const {
    isComplete: isPaymentConfigComplete,
    isValid: isPaymentConfigValid,
    isLoading: isPaymentConfigLoading,
  } = usePaymentWritebackConfig();
  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();
  const { viewClinicPetParent } = useSidePanelSearch();
  const { isMobile } = useResolutionProvider();
  const { trackEvent } = useTracking();
  const { showDisputeNotification, refetchDisputeNotification } = useDisputeNotification();
  const { stripeConnectInstance } = useStripeAccountSession();
  const { isOpen: isDisputeDrawerOpen, onClose: onDisputeDrawerClose, onOpen: onDisputeDrawerOpen } = useDisclosure();
  const { isOpen: isRefundModalOpen, onClose: onRefundModalClose, onOpen: onRefundModalOpen } = useDisclosure();
  const location = useLocation();
  const currentTab = useMemo(() => new URLSearchParams(location.search).get('tab'), [location.search]);

  // State
  const [isDisputeDrawerLoading, setIsDisputeDrawerLoading] = useState(false);
  const [storedTakeForPrint, setStoredTakeForPrint] = useState<number>(DEFAULT_TAKE);
  const [tableWidth, setTableWidth] = useState<number>(0);
  const [excludedTerminals] = usePersistedState<string[]>(PersistedStateKeys.TerminalFilterExclusions, []);
  const [showSubscriptions, setShowSubscriptions] = usePersistedState(
    PersistedStateKeys.ShowSubscriptionsInPaymentReport,
    false,
  );
  const [displayData, setDisplayData] = useState<PaymentReportFinancialTransactionFragment[]>([]);

  const { initialStartDate, initialEndDate } = useMemo(() => {
    const initialStartDate = new Date();
    initialStartDate.setHours(0, 0, 0, 0);

    const initialEndDate = new Date();
    initialEndDate.setHours(23, 59, 59, 999);

    return { initialStartDate, initialEndDate };
  }, []);

  const breakpoint = useBreakpoint();
  const isSmallScreen = useMemo(() => !['xl', '2xl'].includes(breakpoint ?? ''), [breakpoint]);

  const [isPrinting, setIsPrinting] = useState(false);
  const [initialTransactionsLoaded, setInitialTransactionsLoaded] = useState(false);
  const [refundPaymentIntent, setRefundPaymentIntent] = useState<PaymentReportStripePaymentIntent>();
  const [transactionsColumnsSelection, setTransactionsColumnsSelection] = useState<IColumnSelection[]>([
    ...initialTransactionsColumnsSelection,
  ]);
  const [disbursementsColumnsSelection, setDisbursementsColumnsSelection] = useState<IColumnSelection[]>([
    ...initialDisbursementsColumnsSelection,
  ]);
  const [disputesColumnsSelection, setDisputesColumnsSelection] = useState<IColumnSelection[]>([
    ...initialDisputesColumnsSelection,
  ]);
  const [isDeveloperModeEnabled] = usePersistedState(PersistedStateKeys.IsDeveloperModeEnabled, false);
  const [developerModeObject, setDeveloperModeObject] = useState<Record<string, unknown> | null>(null);
  const [resetPagination, setResetPagination] = useState(false);
  const [queryVariables, setQueryVariables] = useState<PaymentReportQueriesVariables>({
    selectedFilterTab: ReportFilterTab.Complete,
    nameFiltering: '',
    cardBrandFiltering: 'All',
    cardTypeFiltering: 'All',
    excludedTerminalNames: excludedTerminals,
    disputeStatusFiltering: 'All',
    showSubscriptions,
    selectionRange: {
      startDate: initialStartDate,
      endDate: initialEndDate,
      key: 'selection',
    },
    take: DEFAULT_TAKE,
    skip: 0,
    sortBy: {
      column: PaymentReportColumnSelectors.Date,
      direction: SortOrder.Desc,
    },
  });
  const [debouncedNameFiltering] = useDebounce(queryVariables.nameFiltering, 350);
  const [selectedDisputeStripeId, setSelectedDisputeStripeId] = useState<string>();

  // Refs
  const printableRef = useRef<HTMLDivElement>(null);

  const isPaymentConfigInvalid = useMemo(() => {
    return (!isPaymentConfigComplete || !isPaymentConfigValid) && !isPaymentConfigLoading;
  }, [isPaymentConfigLoading, isPaymentConfigComplete, isPaymentConfigValid]);

  const clinicUserId = useMemo(() => {
    return clinicUser?.id;
  }, [clinicUser]);

  const showEOD = useMemo(() => {
    return isFeatureEnabled(FeatureFlagName.EODPayoutControls);
  }, [isFeatureEnabled]);

  createTheme('otto', {
    striped: {
      default: '#f1f5f6',
    },
  });

  const showFailedWritebackAlert = useMemo(() => {
    return displayData.some(
      (transaction) =>
        getWritebackStatus({
          writebackStatus: transaction.writebackStatus,
          writebackError: transaction.writebackError,
        }) === PimsWritebackStatus.Failure,
    );
  }, [displayData]);

  const isCarePaymentProcessingClinic = useMemo(
    () => currentClinicId === currentClinic?.carePlanProviderGroup?.paymentProcessorClinicId,
    [currentClinic?.carePlanProviderGroup?.paymentProcessorClinicId, currentClinicId],
  );

  // Apollo
  const {
    data: clinicSettingsData,
    error: clinicSettingsError,
    loading: clinicSettingsLoading,
  } = useQuery(GET_CLINIC_FINANCIAL_REPORT_SETTINGS, {
    variables: {
      id: currentClinicId,
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  const {
    data: transactionsData,
    error: TransactionsError,
    loading: isLoadingTransactions,
    refetch: refetchFinancialTransactions,
  } = useGetFinancialTransactionsQuery({
    variables: {
      data: {
        clinicId: currentClinicId || '',
        reportFilterTab: queryVariables.selectedFilterTab,
        ...(!queryVariables.selectedPayout?.id && {
          timestamp: {
            startDate: queryVariables.selectionRange.startDate.toISOString(),
            endDate: queryVariables.selectionRange.endDate.toISOString(),
          },
        }),
        ...(queryVariables.selectedPayout?.id && {
          stripePayoutId: queryVariables.selectedPayout?.id,
        }),
        clientName: debouncedNameFiltering,
        cardType: queryVariables.cardTypeFiltering !== 'All' ? queryVariables.cardTypeFiltering : undefined,
        cardBrand: queryVariables.cardBrandFiltering !== 'All' ? queryVariables.cardBrandFiltering : undefined,
        includeSubscriptions: queryVariables.showSubscriptions,
        terminalNames: queryVariables.excludedTerminalNames,
      },
      skip: queryVariables.skip,
      take: queryVariables.take,
      sortBy: queryVariables.sortBy,
    },
    skip:
      !currentClinicId ||
      queryVariables.selectedFilterTab === ReportFilterTab.Disbursements ||
      queryVariables.selectedFilterTab === ReportFilterTab.Disputes,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    nextFetchPolicy: GraphQLFetchPolicies.CacheFirst,
    onCompleted: (data) => {
      const _data = data.getFinancialTransactions.data as PaymentReportFinancialTransactionFragment[];
      setDisplayData(_data ?? []);
      setResetPagination(false);
      if (!initialTransactionsLoaded) {
        setInitialTransactionsLoaded(true);
      }
    },
  });

  const {
    data: payoutsData,
    error: PayoutsError,
    loading: isLoadingPayouts,
  } = useGetDisbursementsQuery({
    variables: {
      data: {
        clinicId: currentClinicId || '',
        timestamp: {
          startDate: queryVariables.selectionRange.startDate.toISOString(),
          endDate: queryVariables.selectionRange.endDate.toISOString(),
        },
      },
      skip: queryVariables.skip,
      take: queryVariables.take,
      sortBy: queryVariables.sortBy,
    },
    skip: !currentClinicId || queryVariables.selectedFilterTab !== ReportFilterTab.Disbursements,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    nextFetchPolicy: GraphQLFetchPolicies.CacheFirst,
    onCompleted: () => {
      setResetPagination(false);
      if (!initialTransactionsLoaded) {
        setInitialTransactionsLoaded(true);
      }
    },
  });

  const {
    data: disputesData,
    error: DisputesError,
    loading: isLoadingDisputes,
    refetch: refetchDisputes,
  } = useGetDisputesQuery({
    variables: {
      data: {
        clinicId: currentClinicId || '',
        ...(!queryVariables.selectionRange.shouldIgnore && {
          timestamp: {
            startDate: queryVariables.selectionRange.startDate.toISOString(),
            endDate: queryVariables.selectionRange.endDate.toISOString(),
          },
        }),
        includeSubscriptions: isCarePaymentProcessingClinic ? queryVariables.showSubscriptions : false,
        disputeStatusFiltering:
          queryVariables.disputeStatusFiltering !== 'All' ? queryVariables.disputeStatusFiltering : undefined,
      },
      skip: queryVariables.skip,
      take: queryVariables.take,
      sortBy: queryVariables.sortBy,
    },
    skip: !currentClinicId || queryVariables.selectedFilterTab !== ReportFilterTab.Disputes,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    nextFetchPolicy: GraphQLFetchPolicies.CacheFirst,
    onCompleted: () => {
      setResetPagination(false);
      if (!initialTransactionsLoaded) {
        setInitialTransactionsLoaded(true);
      }
    },
  });

  const [updateFinancialReportSetting] = useMutation(UPDATE_CLINIC_FINANCIAL_REPORT_SETTINGS);

  const isLoadingTableData = useMemo(
    () => isLoadingTransactions || isLoadingPayouts || isLoadingDisputes,
    [isLoadingDisputes, isLoadingPayouts, isLoadingTransactions],
  );

  const totalRows = useMemo(() => {
    switch (queryVariables.selectedFilterTab) {
      case ReportFilterTab.Disbursements:
        return payoutsData?.getDisbursements.total || 0;
      case ReportFilterTab.Disputes:
        return disputesData?.getDisputes.total || 0;
      default:
        return transactionsData?.getFinancialTransactions.total || 0;
    }
  }, [
    disputesData?.getDisputes.total,
    payoutsData?.getDisbursements.total,
    queryVariables.selectedFilterTab,
    transactionsData?.getFinancialTransactions.total,
  ]);

  // Effects
  useEffect(() => {
    // Remove 3rd party range selector
    document.getElementsByClassName('rdrInputRange')[1]?.remove();
  }, []);

  useEffect(() => {
    if (currentTab) {
      const tab = tabs[currentTab as keyof typeof tabs];
      setQueryVariables((prev) => ({
        ...prev,
        selectedFilterTab: tab,
        sortBy:
          tab === ReportFilterTab.Disputes
            ? {
                column: PaymentReportColumnSelectors.Created,
                direction: SortOrder.Desc,
              }
            : {
                column: PaymentReportColumnSelectors.Date,
                direction: SortOrder.Desc,
              },
        selectionRange: {
          ...prev.selectionRange,
          shouldIgnore: true,
        },
      }));
    }
  }, [currentTab]);

  useEffect(() => {
    // Update report data when cache changes
    if (transactionsData?.getFinancialTransactions) {
      setDisplayData(
        (transactionsData?.getFinancialTransactions.data as PaymentReportFinancialTransactionFragment[]) ?? [],
      );
    }
  }, [transactionsData?.getFinancialTransactions]);

  useEffect(() => {
    if (showEOD) {
      setDisbursementsColumnsSelection((columns) => {
        if (!columns.some((column) => column.selector === PaymentReportColumnSelectors.EndOfDay)) {
          columns.splice(1, 0, {
            name: PaymentReportColumnNames.EndOfDay,
            selector: PaymentReportColumnSelectors.EndOfDay,
            sortable: true,
            omit: false,
          });
        }
        return columns;
      });
    }
  }, [showEOD]);

  useEffect(() => {
    if (isClientIdSupported && currentClinicId) {
      setTransactionsColumnsSelection((columns) => {
        if (!columns.some((column) => column.selector === PaymentReportColumnSelectors.ClientId)) {
          columns.splice(1, 0, {
            name: 'CLIENT ID',
            selector: PaymentReportColumnSelectors.ClientId,
            sortable: true,
            omit: false,
          });
        }

        return columns;
      });
    }
  }, [currentClinicId, isClientIdSupported, transactionsColumnsSelection]);

  useEffect(() => {
    if (isFeatureEnabled(FeatureFlagName.InvoiceDiscovery) && isInvoiceDiscoverySupported && !isFeatureFlagsLoading) {
      setTransactionsColumnsSelection((columns) => {
        if (
          !columns.some((column) => column.selector === PaymentReportColumnSelectors.WritebackStatus) &&
          isFeatureEnabled(FeatureFlagName.BitwerxPaymentWritebacks)
        ) {
          columns.splice(2, 0, {
            name: PaymentReportColumnNames.WritebackStatus,
            selector: PaymentReportColumnSelectors.WritebackStatus,
            width: '165px',
            sortable: true,
            omit: false,
          });
        }
        if (!columns.some((column) => column.selector === PaymentReportColumnSelectors.Invoices)) {
          columns.splice(2, 0, {
            name: PaymentReportColumnNames.Invoices,
            selector: PaymentReportColumnSelectors.Invoices,
            width: '145px',
            sortable: false,
            omit: false,
          });
        }
        return columns;
      });
    }
  }, [isFeatureEnabled, isInvoiceDiscoverySupported, isFeatureFlagsLoading, currentClinicId]);

  useEffect(() => {
    if (clinicSettingsError) {
      Sentry.captureException(clinicSettingsError);
      toast({
        status: 'error',
        title: 'Oops, an error occurred while getting your column settings.',
      });
    }
    if (TransactionsError) {
      Sentry.captureException(TransactionsError);
      toast({
        status: 'error',
        title: 'Oops, an error occurred while getting your transactions.',
      });
    }
    if (DisputesError) {
      Sentry.captureException(DisputesError);
      toast({
        status: 'error',
        title: 'Oops, an error occurred while getting your disputes.',
      });
    }
    if (PayoutsError) {
      Sentry.captureException(PayoutsError);
      toast({
        status: 'error',
        title: 'Oops, an error occurred while getting your disbursements.',
      });
    }
  }, [clinicSettingsError, TransactionsError, PayoutsError, toast, DisputesError]);

  useEffect(() => {
    // Use clinic columns in clinicSettings
    if (!isPrinting) {
      const financialReportingConfig = clinicSettingsData?.findUniqueClinic?.clinicSetting?.financialReportingConfig;

      if (financialReportingConfig) {
        setTransactionsColumnsSelection((prev) => {
          const newState = [...prev];
          for (const column of newState) {
            column.omit = financialReportingConfig.transactionColumns?.[column.selector]?.omit || false;
          }
          return newState;
        });
        setDisbursementsColumnsSelection((prev) => {
          const newState = [...prev];
          for (const column of newState) {
            column.omit = financialReportingConfig.disbursementColumns?.[column.selector]?.omit || false;
          }
          return newState;
        });
        setDisputesColumnsSelection((prev) => {
          const newState = [...prev];
          for (const column of newState) {
            column.omit = financialReportingConfig.disputeColumns?.[column.selector]?.omit || false;
          }
          return newState;
        });
      }
    }
  }, [clinicSettingsData, isPrinting]);

  useEffect(() => {
    if (printableRef.current) {
      const width = printableRef.current.clientWidth;
      setTableWidth(width);
    }
  }, []);

  useEffect(() => {
    return (): void => {
      if (stripeConnectInstance) {
        stripeConnectInstance.logout();
      }
    };
  }, [stripeConnectInstance]);

  const handleConversationClick = useCallback(
    (channelId: string) => {
      if (!isMobile) {
        dispatch(openChannelOverlay({ channelId: channelId || '' }));
      } else {
        Object.assign(document.createElement('a'), {
          target: '_blank',
          href: replaceParam(RouteDefinitions.Conversations, ':channelId', channelId),
        }).click();
      }
    },
    [dispatch, isMobile],
  );

  // Data Display
  const tableData = useMemo(() => {
    if (
      displayData &&
      queryVariables.selectedFilterTab !== ReportFilterTab.Disbursements &&
      queryVariables.selectedFilterTab !== ReportFilterTab.Disputes
    ) {
      return buildTransactionsData({
        rawData: displayData,
        displayType: 'Interactive',
        isPaymentConfigInvalid,
        primaryIntegrationName,
        isClientIdSupported,
        hasAdminRoleAtCurrentClinic,
        hasInvoiceDiscovery: isFeatureEnabled(FeatureFlagName.InvoiceDiscovery),
        hasBitwerxPaymentWritebacks: isFeatureEnabled(FeatureFlagName.BitwerxPaymentWritebacks),
        callbacks: {
          onDeveloperLinkClick: (data) => setDeveloperModeObject(data),
          onPetParentClick: (id) => viewClinicPetParent({ clinicPetParentId: id }),
          onConversationClick: (channelId) => handleConversationClick(channelId),
          setRefundPaymentIntent: (intent) => {
            setRefundPaymentIntent(intent);
            onRefundModalOpen();
          },
        },
      });
    }

    const _disputesData = disputesData?.getDisputes.data as StripeDisputeSelectionFragment[];
    if (!!_disputesData && queryVariables.selectedFilterTab === ReportFilterTab.Disputes) {
      return buildDisputesData({
        rawData: _disputesData,
        displayType: 'Interactive',
        hasAdminRoleAtCurrentClinic,
        isDisputeDrawerLoading,
        selectedDisputeStripeId,
        callbacks: {
          onPetParentClick: (id) => viewClinicPetParent({ clinicPetParentId: id }),
          onConversationClick: (channelId) => handleConversationClick(channelId),
          onDrawerOpen: (stripeId) => {
            if (stripeId) {
              setIsDisputeDrawerLoading(true);
              setSelectedDisputeStripeId(stripeId);
              onDisputeDrawerOpen();
            }
          },
        },
      });
    }

    const disbursementsData = payoutsData?.getDisbursements.data as StripePayoutSelectionFragment[];
    if (!!disbursementsData && queryVariables.selectedFilterTab === ReportFilterTab.Disbursements) {
      const disbursementTableData = buildDisbursementsData({
        rawData: disbursementsData,
        displayType: 'Interactive',
        onClickCallback: ({ id, payoutDate }) => {
          if (isFeatureEnabled(FeatureFlagName.CarePlans)) {
            setShowSubscriptions(true);
          }
          setQueryVariables((curr) => ({
            ...curr,
            skip: 0,
            ...(isFeatureEnabled(FeatureFlagName.CarePlans) && { showSubscriptions: true }),
            selectedPayout: { id, payoutDate },
            selectedFilterTab: ReportFilterTab.Complete,
          }));
          setResetPagination(true);
        },
      });

      return disbursementTableData;
    }

    return [];
  }, [
    displayData,
    queryVariables.selectedFilterTab,
    disputesData?.getDisputes.data,
    payoutsData?.getDisbursements.data,
    isPaymentConfigInvalid,
    primaryIntegrationName,
    isClientIdSupported,
    hasAdminRoleAtCurrentClinic,
    isFeatureEnabled,
    viewClinicPetParent,
    handleConversationClick,
    onRefundModalOpen,
    isDisputeDrawerLoading,
    selectedDisputeStripeId,
    onDisputeDrawerOpen,
    setShowSubscriptions,
  ]);

  const handleColumnSelection = useCallback(
    ({
      select,
      prevState,
      columnSelector,
    }: {
      select: boolean;
      prevState: IColumnSelection[];
      columnSelector?: string;
    }): IColumnSelection[] => {
      const newState = [...prevState];
      const financialReportingConfig = clinicSettingsData?.findUniqueClinic?.clinicSetting?.financialReportingConfig;
      let newFinancialReportingConfig: {
        transactionColumns: { [key: string]: { omit?: boolean } };
        disbursementColumns: { [key: string]: { omit?: boolean } };
        disputeColumns: { [key: string]: { omit?: boolean } };
      } = cloneDeep(financialReportingConfig);
      if (!newFinancialReportingConfig) {
        newFinancialReportingConfig = { transactionColumns: {}, disbursementColumns: {}, disputeColumns: {} };
      }
      for (const column of newState) {
        if (columnSelector) {
          if (column.selector === columnSelector) {
            column.omit = !column.omit;
          }
        } else {
          column.omit = !select;
        }
      }

      let columns;

      switch (queryVariables.selectedFilterTab) {
        case ReportFilterTab.Disbursements:
          columns = newFinancialReportingConfig?.disbursementColumns;
          break;
        case ReportFilterTab.Disputes:
          columns = newFinancialReportingConfig?.disputeColumns;
          break;
        default:
          columns = newFinancialReportingConfig?.transactionColumns;
          break;
      }

      for (const column of newState) {
        if (!columns) {
          columns = {};
        }
        if (!columns[column.selector]) {
          columns[column.selector] = {};
        }
        columns[column.selector].omit = column.omit;
      }
      updateFinancialReportSetting({
        variables: {
          where: { id: clinicSettingsData?.findUniqueClinic?.clinicSetting?.id },
          data: {
            financialReportingConfig: {
              transactionColumns: newFinancialReportingConfig.transactionColumns,
              disbursementColumns: newFinancialReportingConfig.disbursementColumns,
            },
          },
        },
      });

      return newState;
    },
    [
      clinicSettingsData?.findUniqueClinic?.clinicSetting?.financialReportingConfig,
      clinicSettingsData?.findUniqueClinic?.clinicSetting?.id,
      queryVariables.selectedFilterTab,
      updateFinancialReportSetting,
    ],
  );

  const handleDisbursementsColumnSelectionAll = useCallback(
    (select: boolean): void => {
      setDisbursementsColumnsSelection((prevState) => {
        const newState = handleColumnSelection({ select, prevState });
        return newState;
      });
    },
    [handleColumnSelection],
  );

  const handleDisputesColumnSelectionAll = useCallback(
    (select: boolean): void => {
      setDisputesColumnsSelection((prevState) => {
        const newState = handleColumnSelection({ select, prevState });
        return newState;
      });
    },
    [handleColumnSelection],
  );

  const handleDisbursementsColumnSelectionToggle = useCallback(
    (columnSelector: string): void => {
      setDisbursementsColumnsSelection((prevState) => {
        const newState = handleColumnSelection({ select: false, prevState, columnSelector });
        return newState;
      });
    },
    [handleColumnSelection],
  );

  const handleDisputesColumnSelectionToggle = useCallback(
    (columnSelector: string): void => {
      setDisputesColumnsSelection((prevState) => {
        const newState = handleColumnSelection({ select: false, prevState, columnSelector });
        return newState;
      });
    },
    [handleColumnSelection],
  );

  const handleTransactionsColumnSelectionAll = useCallback(
    (select: boolean): void => {
      setTransactionsColumnsSelection((prevState) => {
        const newState = handleColumnSelection({ select, prevState });
        return newState;
      });
    },
    [handleColumnSelection],
  );

  const handleTransactionsColumnSelectionToggle = useCallback(
    (columnSelector: string): void => {
      setTransactionsColumnsSelection((prevState) => {
        const newState = handleColumnSelection({ select: false, prevState, columnSelector });
        return newState;
      });
    },
    [handleColumnSelection],
  );

  // Printing
  const handlePrint = useReactToPrint({
    content: () => printableRef.current,
    onAfterPrint: () => {
      setIsPrinting(false);
      setQueryVariables((curr) => ({
        ...curr,
        skip: 0,
        take: storedTakeForPrint,
      }));
      setResetPagination(true);
    },
    copyStyles: true,
    documentTitle: `Otto_Financial_Report_${format(new Date(), "MM-dd-yy'_'_h_mm_aaaaa'm'")}`,
    pageStyle: () => {
      return `
        @page {
          size: ${2 * 297}mm ${2 * 210}mm
        }

        @media all {
          .pagebreak {
            display: none;
          }
       }

        @media print {
          .pagebreak {
            page-break-before: always;
          }

          .dispute-popover-anchor {
            display: none;
          }
        }`;
    },
  });

  useEffect(() => {
    if (isPrinting && tableData.length === totalRows && !isLoadingTableData) {
      handlePrint();
    }
  }, [handlePrint, isLoadingTableData, isPrinting, tableData.length, totalRows]);

  const onPrintClick = useCallback(() => {
    if (queryVariables.selectedFilterTab === ReportFilterTab.Disbursements) {
      setDisbursementsColumnsSelection((state) => {
        const newState = [...state];
        for (const column of newState) {
          const selector = column.selector;
          if (selector === PaymentReportColumnSelectors.LinkedTransactions) {
            column.omit = true;
          }
        }
        return newState;
      });
    } else if (queryVariables.selectedFilterTab === ReportFilterTab.Disputes) {
      setDisputesColumnsSelection((state) => {
        const newState = [...state];
        for (const column of newState) {
          const selector = column.selector;
          if (selector === PaymentReportColumnSelectors.Manage || selector === PaymentReportColumnSelectors.Actions) {
            column.omit = true;
          }
        }

        return newState;
      });
    } else {
      setTransactionsColumnsSelection((state) => {
        const newState = [...state];
        for (const column of newState) {
          const selector = column.selector;
          if (selector === PaymentReportColumnSelectors.Conversation) {
            column.omit = true;
          }
        }

        return newState;
      });
    }

    setIsPrinting(true);

    if (queryVariables.take !== totalRows) {
      // Store the current user selection
      setStoredTakeForPrint(queryVariables.take);

      // Fetch the full data set
      setQueryVariables((curr) => ({
        ...curr,
        skip: 0,
        take: totalRows,
      }));
      setResetPagination(true);
    }

    if (!tableData.length) {
      setIsPrinting(false);
      toast({
        status: 'error',
        title: 'There is no data to print, please adjust your filters',
      });
    }
  }, [queryVariables.take, queryVariables.selectedFilterTab, tableData.length, toast, totalRows]);

  // Terminals
  const terminalOptions = useMemo((): MenuItemProps[] => {
    const locations = currentClinic?.stripeTerminalLocations ?? [];
    const terminals = locations.flatMap((location) => location.terminals);
    const uniqTerminals = uniqBy(terminals, 'name');

    return sortBy(
      uniqTerminals.map((terminal) => ({
        label: terminal.name,
        value: terminal.name,
        isSelected: !queryVariables.excludedTerminalNames.includes(terminal.name),
      })),
      [(item): string => item.label.toLowerCase()],
    );
  }, [currentClinic?.stripeTerminalLocations, queryVariables.excludedTerminalNames]);

  const onTerminalSelect = useCallback(
    (item: MenuItemProps) => {
      if (item.value === undefined) return;
      // need to update state (to trigger render) and local storage (for saving filter for session duration)
      if (item.isSelected) {
        setQueryVariables((curr) => ({
          ...curr,
          skip: 0,
          excludedTerminalNames: [...curr.excludedTerminalNames].filter((val) => val !== item.value),
        }));
      } else {
        setQueryVariables((curr) => ({
          ...curr,
          skip: 0,
          excludedTerminalNames: [...curr.excludedTerminalNames, item.value as string],
        }));
      }

      setResetPagination(true);
    },
    [setResetPagination],
  );

  const onTerminalSelectAll = useCallback(
    (selectedItems: MenuItemProps[]) => {
      const selectedNames = selectedItems.map((item) => item.value);
      const allTerminalNames = terminalOptions.map((option) => option.value || '').filter((val) => !!val);
      const terminalNamesToExclude = allTerminalNames.filter((name) => !selectedNames.includes(name));
      setQueryVariables((curr) => ({
        ...curr,
        skip: 0,
        excludedTerminalNames: terminalNamesToExclude,
      }));
      setResetPagination(true);
    },
    [setResetPagination, terminalOptions],
  );

  // Table columns
  const tableColumns = useMemo(() => {
    switch (queryVariables.selectedFilterTab) {
      case ReportFilterTab.Disbursements:
        return disbursementsColumnsSelection;
      case ReportFilterTab.Disputes:
        return disputesColumnsSelection;
      default:
        return transactionsColumnsSelection;
    }
  }, [
    disbursementsColumnsSelection,
    disputesColumnsSelection,
    queryVariables.selectedFilterTab,
    transactionsColumnsSelection,
  ]);

  const columnFilterOptions: MenuItemProps[] = useMemo(() => {
    return tableColumns.map((column) => ({
      label: column.name,
      value: column.name,
      isSelected: !column.omit,
      onSelect: (): void => {
        if (queryVariables.selectedFilterTab === ReportFilterTab.Disbursements) {
          handleDisbursementsColumnSelectionToggle(column.selector);
        } else if (queryVariables.selectedFilterTab === ReportFilterTab.Disputes) {
          handleDisputesColumnSelectionToggle(column.selector);
        } else {
          handleTransactionsColumnSelectionToggle(column.selector);
        }
      },
    }));
  }, [
    handleDisbursementsColumnSelectionToggle,
    handleDisputesColumnSelectionToggle,
    handleTransactionsColumnSelectionToggle,
    queryVariables.selectedFilterTab,
    tableColumns,
  ]);

  const onColumnSelectAll = useCallback(
    (selectedItems: MenuItemProps[]) => {
      const val = !!selectedItems?.length;
      if (queryVariables.selectedFilterTab === ReportFilterTab.Disbursements) {
        handleDisbursementsColumnSelectionAll(val);
      } else if (queryVariables.selectedFilterTab === ReportFilterTab.Disputes) {
        handleDisputesColumnSelectionAll(val);
      } else {
        handleTransactionsColumnSelectionAll(val);
      }
    },
    [
      handleDisbursementsColumnSelectionAll,
      handleDisputesColumnSelectionAll,
      handleTransactionsColumnSelectionAll,
      queryVariables.selectedFilterTab,
    ],
  );

  // Disputes
  const handleDisputeDrawerClose = useCallback(() => {
    setSelectedDisputeStripeId(undefined);
    refetchDisputes();
    refetchDisputeNotification();
    onDisputeDrawerClose();
  }, [onDisputeDrawerClose, refetchDisputeNotification, refetchDisputes]);

  // Filtering options
  const paginationRowsPerPageOptions = useMemo(() => {
    const options = [DEFAULT_TAKE];
    let counter = DEFAULT_TAKE;

    while (counter < totalRows) {
      counter += 5;

      if (counter > 30) {
        options.push(totalRows);
        counter += totalRows;
      } else {
        options.push(counter);
      }
    }

    return options;
  }, [totalRows]);

  // Date filter
  const dateSelectionString = useMemo(() => {
    if (queryVariables.selectedPayout?.payoutDate) {
      return `Transactions from disbursement on ${queryVariables.selectedPayout?.payoutDate}`;
    }

    if (queryVariables.selectedFilterTab === ReportFilterTab.Disputes && queryVariables.selectionRange.shouldIgnore) {
      return 'All disputes';
    }

    return `${format(new Date(queryVariables.selectionRange.startDate), "MM-dd-yy h:mm aaaaa'm' ")}-
              ${format(new Date(queryVariables.selectionRange.endDate), "MM-dd-yy h:mm aaaaa'm'")}`;
  }, [queryVariables]);

  const handleTimeChange = useCallback(({ date, type }: { date: Date; type: 'startDate' | 'endDate' }): void => {
    setQueryVariables((curr) => {
      return {
        ...curr,
        skip: 0,
        selectionRange: {
          ...curr.selectionRange,
          [type]: new Date(
            curr.selectionRange[type].getFullYear(),
            curr.selectionRange[type].getMonth(),
            curr.selectionRange[type].getDate(),
            date.getHours(),
            date.getMinutes(),
            0,
            0,
          ),
          shouldIgnore: false,
        },
      };
    });
  }, []);

  return (
    <Box pt={5}>
      {showEOD && (
        <Flex mb="4">
          <EODBanner
            pimsName={primaryIntegrationName || 'your PIMS'}
            currentClinicId={currentClinicId}
            clinicUserId={clinicUserId}
            isEODAutomated={!!currentClinic?.payoutBatchingSetting?.isAutomated}
          />
        </Flex>
      )}
      <WritebackAlerts showFailedWritebackAlert={showFailedWritebackAlert} />
      {isPrinting && (
        <Spinner
          isFullScreen
          showLabel
          label="Preparing report for print..."
          size="xl"
          spinnerColor="background.default"
          labelProps={{ variant: 'onContrast' }}
          backdropColor="background.backdrop"
        />
      )}
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="sm">Transaction Reporting</Heading>
        <Flex align="center" gap={4}>
          {queryVariables.selectedFilterTab !== ReportFilterTab.Disbursements &&
            isFeatureEnabled(FeatureFlagName.CarePlans) && (
              <Checkbox
                isChecked={showSubscriptions}
                defaultChecked={showSubscriptions}
                onChange={(): void => {
                  trackEvent(
                    showSubscriptions
                      ? CareMembershipEvents.PaymentsReportExclude
                      : CareMembershipEvents.PaymentsReportInclude,
                  );
                  setShowSubscriptions((shown) => !shown);
                  setQueryVariables((curr) => ({
                    ...curr,
                    skip: 0,
                    showSubscriptions: !curr.showSubscriptions,
                  }));
                  setResetPagination(true);
                }}
                {...(queryVariables.selectedFilterTab === ReportFilterTab.Disputes &&
                  !isCarePaymentProcessingClinic && { display: 'none' })}
              >
                Include Memberships
              </Checkbox>
            )}
          <Box>
            <Popover
              anchor={
                <Button
                  iconName="calendarDate"
                  variant="secondary"
                  size="sm"
                  rightIcon={<Icon size="sm" name="chevronDown" />}
                >
                  Date
                </Button>
              }
              variant="default"
              size="xl"
              contentProps={{ w: 'auto' }}
            >
              <Grid templateColumns="repeat(8, 1fr)" pt={1} pb={3}>
                <GridItem colSpan={2} />
                <GridItem colSpan={3}>
                  <Flex alignItems="center" justifyContent="center">
                    <Text as="p" size="sm">
                      Start at
                    </Text>
                    <Box maxW="100px" mx={2}>
                      <TimePicker
                        initialTime={queryVariables.selectionRange.startDate}
                        onTimeChange={(date): void => {
                          handleTimeChange({ date, type: 'startDate' });
                          setResetPagination(true);
                        }}
                        inputProps={{ rightElement: undefined, size: 'sm' }}
                        placement="bottom"
                      />
                    </Box>
                    <Text as="p" size="sm">
                      on:
                    </Text>
                  </Flex>
                </GridItem>
                <GridItem colSpan={3}>
                  <Flex alignItems="center" justifyContent="center">
                    <Text as="p" size="sm">
                      End at
                    </Text>
                    <Box maxW="100px" mx={2}>
                      <TimePicker
                        initialTime={queryVariables.selectionRange.endDate}
                        onTimeChange={(date): void => {
                          handleTimeChange({ date, type: 'endDate' });
                          setResetPagination(true);
                        }}
                        inputProps={{ rightElement: undefined, size: 'sm' }}
                        placement="bottom"
                      />
                    </Box>
                    <Text as="p" size="sm">
                      on:
                    </Text>
                  </Flex>
                </GridItem>
              </Grid>
              <DateRangePicker
                showSelectionPreview={true}
                moveRangeOnFirstSelection={false}
                months={isSmallScreen ? 1 : 2}
                rangeColors={['#4F830F']}
                fixedHeight={true}
                direction="horizontal"
                ranges={[queryVariables.selectionRange]}
                // eslint-disable-next-line
                onChange={(e: any): void => {
                  setQueryVariables((curr) => ({
                    ...curr,
                    skip: 0,
                    selectionRange: {
                      ...curr.selectionRange,
                      startDate: new Date(e.selection.startDate.setHours(0, 0, 0, 0)),
                      endDate: new Date(e.selection.endDate.setHours(23, 59, 59, 999)),
                      shouldIgnore: false,
                    },
                    ...(!!curr.selectedPayout && { selectedPayout: undefined }),
                  }));
                  setResetPagination(true);
                }}
              />
            </Popover>
          </Box>
          {queryVariables.selectedFilterTab !== ReportFilterTab.Disbursements &&
            queryVariables.selectedFilterTab !== ReportFilterTab.Disputes && (
              <TerminalFilterMenu
                menuItems={terminalOptions}
                onSelect={onTerminalSelect}
                onSelectAll={onTerminalSelectAll}
              />
            )}
          <ColumnFilterMenu
            menuItems={columnFilterOptions}
            onSelectAll={(selectedItems): void => onColumnSelectAll(selectedItems)}
          />
          <ExportMenu
            isLoadingTableData={isLoadingTableData}
            printProps={{
              onPrint: onPrintClick,
              isPrinting,
            }}
            downloadProps={{
              columns: tableColumns,
              clinicId: currentClinicId || '',
              isPaymentConfigInvalid,
              primaryIntegrationName,
              isClientIdSupported,
              queryVariables,
            }}
          />
        </Flex>
      </Flex>
      <Box bgColor="background.default" px={4} pb={5}>
        <Flex
          justifyContent="space-between"
          alignItems="center"
          bgColor="background.default"
          borderBottomWidth="2px"
          borderColor="#e8e9e9"
          w="100%"
        >
          <Flex>
            <FilterTab
              tab={ReportFilterTab.Complete}
              selectedFilterTab={queryVariables.selectedFilterTab}
              setSelectedFilterTab={(selectedTab): void =>
                setQueryVariables((curr) => {
                  return {
                    ...curr,
                    selectedFilterTab: selectedTab,
                  };
                })
              }
            />
            <FilterTab
              tab={ReportFilterTab.Incomplete}
              selectedFilterTab={queryVariables.selectedFilterTab}
              setSelectedFilterTab={(selectedTab): void =>
                setQueryVariables((curr) => {
                  return {
                    ...curr,
                    selectedFilterTab: selectedTab,
                  };
                })
              }
            />
            <FilterTab
              tab={ReportFilterTab.All}
              selectedFilterTab={queryVariables.selectedFilterTab}
              setSelectedFilterTab={(selectedTab): void =>
                setQueryVariables((curr) => {
                  return {
                    ...curr,
                    selectedFilterTab: selectedTab,
                  };
                })
              }
            />
            <FilterTab tab="Spacer" />
            <FilterTab
              tab={ReportFilterTab.Disbursements}
              selectedFilterTab={queryVariables.selectedFilterTab}
              setSelectedFilterTab={(selectedTab): void =>
                setQueryVariables((curr) => {
                  return {
                    ...curr,
                    selectedFilterTab: selectedTab,
                    sortBy: { column: PaymentReportColumnSelectors.Created, direction: SortOrder.Asc },
                  };
                })
              }
            />
            <FilterTab
              tab={ReportFilterTab.Disputes}
              selectedFilterTab={queryVariables.selectedFilterTab}
              setSelectedFilterTab={(selectedTab): void => {
                const showSubscriptions = isFeatureEnabled(FeatureFlagName.CarePlans);
                setShowSubscriptions(showSubscriptions);
                setQueryVariables((curr) => {
                  return {
                    ...curr,
                    selectedFilterTab: selectedTab,
                    showSubscriptions,
                    sortBy: { column: PaymentReportColumnSelectors.Created, direction: SortOrder.Desc },
                    selectionRange: { ...curr.selectionRange, shouldIgnore: true },
                  };
                });
              }}
              rightIcon={showDisputeNotification ? <Icon name="warningSign" variant="danger" size="xs" /> : undefined}
            />
          </Flex>
          <Flex alignItems="center">
            {queryVariables.selectedFilterTab === ReportFilterTab.Disputes &&
              (!queryVariables.selectionRange.shouldIgnore ||
                (isFeatureEnabled(FeatureFlagName.CarePlans) && !queryVariables.showSubscriptions)) && (
                <Button
                  variant="ghost"
                  size="sm"
                  mr={2}
                  onClick={(): void => {
                    const showSubscriptions = isFeatureEnabled(FeatureFlagName.CarePlans);
                    setShowSubscriptions(showSubscriptions);
                    setQueryVariables((curr) => {
                      return {
                        ...curr,
                        showSubscriptions,
                        selectionRange: { ...curr.selectionRange, shouldIgnore: true },
                        skip: 0,
                      };
                    });
                    setResetPagination(true);
                  }}
                >
                  Show All Disputes
                </Button>
              )}
            <Text size="sm">{dateSelectionString}</Text>
          </Flex>
        </Flex>
      </Box>
      {queryVariables.selectedFilterTab !== ReportFilterTab.Disbursements && (
        <ReportFilters
          queryVariables={queryVariables}
          setQueryVariables={setQueryVariables}
          setResetPagination={setResetPagination}
        />
      )}
      <Box ref={printableRef}>
        {queryVariables.selectedFilterTab !== ReportFilterTab.Disputes && (
          <TotalsTable
            clinicId={currentClinicId || ''}
            payoutId={queryVariables.selectedPayout?.id}
            filters={{
              cardBrandFiltering: queryVariables.cardBrandFiltering,
              cardTypeFiltering: queryVariables.cardTypeFiltering,
              nameFiltering: debouncedNameFiltering,
              excludedTerminalNames: queryVariables.excludedTerminalNames,
              selectionRange: queryVariables.selectionRange,
              showSubscriptions: queryVariables.showSubscriptions,
            }}
            selectedFilterTab={queryVariables.selectedFilterTab}
            disbursementsColumnsSelection={disbursementsColumnsSelection}
            transactionsColumnsSelection={transactionsColumnsSelection}
          />
        )}
        <StyledDataTable
          noHeader
          persistTableHead
          striped
          columns={[
            ...tableColumns,
            ...(isDeveloperModeEnabled &&
            queryVariables.selectedFilterTab !== ReportFilterTab.Disputes &&
            queryVariables.selectedFilterTab !== ReportFilterTab.Disbursements
              ? [
                  {
                    name: PaymentReportColumnNames.DevMode,
                    selector: PaymentReportColumnSelectors.DevMode,
                    sortable: false,
                    omit: false,
                    width: '260px',
                  },
                ]
              : []),
          ]}
          data={tableData}
          progressPending={
            (clinicSettingsLoading || !initialTransactionsLoaded || isLoadingTableData) &&
            !TransactionsError &&
            !isPrinting
          }
          progressComponent={
            <Box py={3}>
              <Spinner />
            </Box>
          }
          noDataComponent={
            <Box py={3}>
              <Text size="sm" variant="subtle">
                There are no records to display
              </Text>
            </Box>
          }
          customStyles={{
            progress: { style: { width: tableWidth, position: 'sticky', left: 0 } },
            noData: { style: { width: tableWidth, position: 'sticky', left: 0 } },
          }}
          paginationServer
          paginationDefaultPage={1}
          paginationResetDefaultPage={resetPagination}
          paginationTotalRows={totalRows}
          pagination={!isPrinting}
          paginationPerPage={queryVariables.take}
          paginationRowsPerPageOptions={paginationRowsPerPageOptions}
          onChangeRowsPerPage={(currentVal): void => {
            setQueryVariables((curr) => ({ ...curr, skip: 0, take: currentVal }));
            setResetPagination(true);
          }}
          onChangePage={(page): void => {
            const skip = (page - 1) * queryVariables.take;
            setQueryVariables((curr) => ({ ...curr, skip }));
          }}
          theme="otto"
          sortIcon={<Icon name="chevronDown" size="sm" />}
          sortServer
          onSort={(column, sortDirection): void => {
            const sortBy = { column: `${column.selector}`, direction: sortDirection as SortOrder };
            setQueryVariables((curr) => ({ ...curr, skip: 0, sortBy }));
            setResetPagination(true);
          }}
        />
      </Box>
      {refundPaymentIntent && (
        <RefundModal
          isOpen={isRefundModalOpen}
          onClose={onRefundModalClose}
          paymentIntentId={refundPaymentIntent.id}
          refetchStripePaymentIntent={(): Promise<ApolloQueryResult<GetFinancialTransactionsQuery>> =>
            refetchFinancialTransactions()
          }
        />
      )}
      {queryVariables.selectedFilterTab === ReportFilterTab.Disputes && stripeConnectInstance && (
        <ConnectComponentsProvider connectInstance={stripeConnectInstance}>
          {selectedDisputeStripeId && isDisputeDrawerOpen && (
            <DisputeDrawer
              stripeId={selectedDisputeStripeId}
              onClose={handleDisputeDrawerClose}
              setIsDisputeDrawerLoading={setIsDisputeDrawerLoading}
            />
          )}
        </ConnectComponentsProvider>
      )}
      {isDeveloperModeEnabled && (
        <CModal size={'2xl'} isOpen={!!developerModeObject} onClose={(): void => setDeveloperModeObject(null)}>
          <ModalOverlay />
          <ModalContent>
            <CModalHeader>Financial Transaction {developerModeObject?.id}</CModalHeader>
            <ModalCloseButton />
            <CModalBody>
              <Box h="75vh" overflowY="auto">
                <DevModeDisplayJSON src={developerModeObject || {}} />
              </Box>
            </CModalBody>
          </ModalContent>
        </CModal>
      )}
    </Box>
  );
};

export default PaymentReport;

const StyledDataTable = styled(DataTable)`
  min-height: 300px;
  font-size: 14px;

  [data-popper-arrow] {
    overflow: visible !important;
  }
`;
